import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowRightLeft, Printer, RefreshCw, FileText, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { SECTION_MODULES } from '@/lib/sectionModules';
import {
  fmtDate, openBlankPrintWindow, fillPrintWindow, failPrintWindow,
  buildTransferSheetHtml, buildBundleWiseHtml, buildTransferReportHtml,
} from '@/lib/itPrintSheets';
import {
  getModuleInternalTransfers, getModuleInternalTransferDetail, saveModuleInternalTransferErp,
} from '@/services/sectionReportsApi';
import { ReportHeader, StatCard, DateSearchFilters, Pager, UnknownSection } from '@/components/sectionReports/ReportParts';

const PAGE_SIZE = 50;
const EMPTY = { dateFrom: '', dateTo: '', search: '' };

const statusClass = (s) => {
  const v = (s || '').toLowerCase();
  if (v.includes('reject')) return 'bg-red-100 text-red-700 border-red-200';
  if (v.includes('complete') || v === 'received') return 'bg-green-100 text-green-700 border-green-200';
  if (v === 'shipped') return 'bg-blue-100 text-blue-700 border-blue-200';
  return 'bg-amber-100 text-amber-700 border-amber-200';
};

const toParams = (f, extra) => ({
  page_size: PAGE_SIZE, ...extra,
  date_from: f.dateFrom || undefined, date_to: f.dateTo || undefined, search: f.search.trim() || undefined,
});

const RowPrintButtons = ({ row, busy, onPrint }) => (
  <div className="flex items-center justify-center gap-1">
    {[['transfer', FileText, 'Print Transfer Sheet'], ['bundlewise', Layers, 'Print Bundle-Wise sheet']].map(([kind, Icon, title]) => (
      <Button key={kind} variant="outline" size="sm" className="h-8 px-2" title={title} disabled={busy !== ''}
        onClick={() => onPrint(row, kind)} data-testid={`print-${kind}-${row.transfer_id}`}>
        {busy === `${row.transfer_id}:${kind}` ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
      </Button>
    ))}
  </div>
);

const SectionITReport = ({ sectionId: propSectionId }) => {
  const params = useParams();
  const sectionId = propSectionId || params.sectionId;
  const mod = SECTION_MODULES[sectionId];
  const [data, setData] = useState({ rows: [], total: 0, total_pages: 1, page: 1, summary: {} });
  const [filters, setFilters] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [rowPrinting, setRowPrinting] = useState('');
  const [erp, setErp] = useState({});
  const [erpSaving, setErpSaving] = useState('');

  const load = useCallback(async (page = 1, f = filters) => {
    if (!mod) return;
    setLoading(true);
    try {
      const res = await getModuleInternalTransfers(mod.key, toParams(f, { page }));
      setData(res);
      setErp(Object.fromEntries((res.rows || []).map((r) => [r.transfer_id, r.erp_entry_no || ''])));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load internal transfer report');
    } finally {
      setLoading(false);
    }
  }, [mod, filters]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(1); }, [sectionId]);

  if (!mod) return <UnknownSection sectionId={sectionId} />;

  const saveErp = async (r) => {
    const val = (erp[r.transfer_id] ?? '').trim();
    if (val === (r.erp_entry_no || '')) return;
    setErpSaving(r.transfer_id);
    try {
      await saveModuleInternalTransferErp(mod.key, r.transfer_id, val);
      setData((d) => ({ ...d, rows: d.rows.map((x) => (x.transfer_id === r.transfer_id ? { ...x, erp_entry_no: val } : x)) }));
      toast.success(`ERP entry saved for ${r.transfer_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save ERP entry');
    } finally {
      setErpSaving('');
    }
  };

  const printRow = async (r, kind) => {
    const win = openBlankPrintWindow();
    if (!win) { toast.error('Popup blocked — allow popups to print the sheet'); return; }
    setRowPrinting(`${r.transfer_id}:${kind}`);
    try {
      const t = await getModuleInternalTransferDetail(mod.key, r.transfer_id);
      if (!(t.bundles || []).length) { failPrintWindow(win, 'No bundle detail found for this transfer.'); return; }
      fillPrintWindow(win, kind === 'bundlewise' ? buildBundleWiseHtml(t, mod.name) : buildTransferSheetHtml(t, mod.name));
    } catch (err) {
      failPrintWindow(win, 'Failed to load transfer detail for printing.');
      toast.error('Failed to load transfer detail for printing');
    } finally {
      setRowPrinting('');
    }
  };

  const printReport = async () => {
    const win = openBlankPrintWindow();
    if (!win) { toast.error('Popup blocked — allow popups to print the report'); return; }
    setPrinting(true);
    try {
      const res = await getModuleInternalTransfers(mod.key, toParams(filters, { page: 1, page_size: 500 }));
      if (!(res.rows || []).length) { failPrintWindow(win, 'No transfers to print.'); return; }
      const range = (filters.dateFrom || filters.dateTo) ? `${filters.dateFrom || '…'} to ${filters.dateTo || '…'}` : 'All dates';
      fillPrintWindow(win, buildTransferReportHtml(res.rows, mod.name, range, res.total));
    } catch (err) {
      failPrintWindow(win, 'Failed to prepare the printable report.');
    } finally {
      setPrinting(false);
    }
  };

  const rows = data.rows || [];
  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="section-it-report">
      <ReportHeader icon={ArrowRightLeft} title="Internal Transfer Report" subtitle={`Transfers out of ${mod.name}`}
        backRoute={`/sections/${sectionId}`}
        actions={(
          <Button variant="outline" onClick={printReport} disabled={printing || loading}
            className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="print-report-btn">
            {printing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />} Print Report
          </Button>
        )} />
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="grid grid-cols-2 gap-4 mb-6">
          <StatCard label="Total Transfers" value={(data.summary?.total_transfers || 0).toLocaleString()} icon={ArrowRightLeft} testId="stat-transfers" />
          <StatCard label="Total Bundles" value={(data.summary?.total_bundles || 0).toLocaleString()} icon={Layers} testId="stat-bundles" tone="text-indigo-500" />
        </div>
        <DateSearchFilters filters={filters} loading={loading} placeholder="Transfer ID, destination, staff, status, bundle, ERP no…"
          onChange={(p) => setFilters((f) => ({ ...f, ...p }))} onSearch={() => load(1)}
          onClear={() => { setFilters(EMPTY); load(1, EMPTY); }} />
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    {['Transfer ID', 'Date', 'To Section', 'Type', 'Bundles', 'Qty', 'Staff', 'Status', 'Remarks', 'ERP Entry No', 'Print'].map((h) => (
                      <TableHead key={h} className={`font-semibold ${['Bundles', 'Qty'].includes(h) ? 'text-right' : ''}`}>{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={11} className="text-center py-8"><RefreshCw className="w-6 h-6 animate-spin mx-auto" /></TableCell></TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow><TableCell colSpan={11} className="text-center py-8 text-gray-500" data-testid="empty-row">No internal transfers found. Try adjusting the filters.</TableCell></TableRow>
                  ) : rows.map((r) => (
                    <TableRow key={r.transfer_id} className="hover:bg-gray-50" data-testid={`it-row-${r.transfer_id}`}>
                      <TableCell className="font-mono text-sm font-semibold">{r.transfer_id}</TableCell>
                      <TableCell className="text-sm whitespace-nowrap">{fmtDate(r.date)}</TableCell>
                      <TableCell>{r.to_section || '—'}</TableCell>
                      <TableCell><Badge variant="outline" className={r.transfer_type === 'REWORK' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'}>{r.transfer_type}</Badge></TableCell>
                      <TableCell className="text-right font-semibold" data-testid={`it-bundles-${r.transfer_id}`}>{r.bundle_count}</TableCell>
                      <TableCell className="text-right font-semibold text-[#2D5F4F]" data-testid={`it-qty-${r.transfer_id}`}>{r.qty}{r.qty_unit === 'Mtr' ? ' m' : ''}</TableCell>
                      <TableCell className="text-sm">{r.staff_name || '—'}{r.staff_id ? <span className="text-gray-400"> ({r.staff_id})</span> : null}</TableCell>
                      <TableCell><Badge variant="outline" className={statusClass(r.status)}>{r.status || '—'}</Badge></TableCell>
                      <TableCell className="text-sm max-w-[200px] truncate" title={r.remarks}>{r.remarks || '—'}</TableCell>
                      <TableCell>
                        <Input value={erp[r.transfer_id] ?? ''} placeholder="ERP no…" className="h-8 w-32 text-sm"
                          disabled={erpSaving === r.transfer_id}
                          onChange={(e) => setErp((p) => ({ ...p, [r.transfer_id]: e.target.value }))}
                          onBlur={() => saveErp(r)} onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                          data-testid={`erp-input-${r.transfer_id}`} />
                      </TableCell>
                      <TableCell><RowPrintButtons row={r} busy={rowPrinting} onPrint={printRow} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <Pager page={data.page || 1} pageSize={PAGE_SIZE} total={data.total || 0} totalPages={data.total_pages || 1}
              loading={loading} onPage={(p) => load(p)} noun="transfers" />
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default SectionITReport;
